
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../utils/constants';
import auth from '../../Auth';
import JsonEditorViewer from './JsonEditorViewer';
import { Calendar, Loader2 } from 'lucide-react';
import Breadcrumbs from '../Breadcrumbs';
import { Button, Textarea } from '@nextui-org/react';
import { toast } from 'react-toastify';
import { TOAST_STYLE_ERROR, TOAST_STYLE_SUCCESS } from '../../utils/constants';
import { Input } from '@nextui-org/react';

const CRUDTest = () => {

    const { prompt_id, test_id } = useParams();

    const [promptChats, setChats] = useState([{ role: 'system' }]);
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const versionParam = query.get('version', '');
    const [logTimestamps, setLogTimestamps] = useState([]);
    const [promptVersion, setVersion] = useState(versionParam);
  
    const [timestamp, setTimestamp] = useState('test_context');
    const [testContext, setTestContext] = useState('{}');
    const [realTestContext, setRealTestContext] = useState('{}');
    const [idealAnswer, setIdealAnswer] = useState("");
    const [generatedFacts, setGeneratedFacts] = useState([]);

    const [generatedTest, setGeneratedTest] = useState("");
    const [testThreshold, setTestThreshold] = useState(75);
    const [testName, setTestName] = useState("")
    const [isFetchedTest, setIsFetchedTest] = useState(false);
    const [isFetchedTimestamps, setIsFetchedTimestamps] = useState(false);

    const navigate = useNavigate()

    const fetchTimestamps = async () => {
        const response = await axios.get(`${API_URL}/logs/timestamps?prompt_id=${prompt_id}`, {
          headers: {
            Authorization: `Bearer ${await auth.getIdToken()}`
          }
        });
    
        if (response.status === 200) {
          const log_timestamps = response.data;
          setLogTimestamps(log_timestamps.timestamps.sort((a, b) => Number(b) - Number(a)));
          setIsFetchedTimestamps(true)
        }
      };

      const deleteTest = async () => {
        await axios.delete(`${API_URL}/tests?deleteTest&test_id=${test_id}&prompt_id=${prompt_id}`,
          {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          },
            ).then(() =>{ 
              toast.success(`Test is deleted!`, TOAST_STYLE_SUCCESS)
              navigate(`/tests/${prompt_id}`)
            }).catch(err => {
              toast.error(`Error deleting the test. Try again!`, TOAST_STYLE_ERROR)
              console.log(err)}
            )
      }

      const fetchTestData = async () => {
        await axios.get(`${API_URL}/tests?getTest&prompt_id=${prompt_id}&test_id=${test_id}`, {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`
            }
          })
        .then(res => {
            setIdealAnswer(res.data.ideal_answer)
            setTestContext(JSON.stringify(JSON.parse(res.data.test_context), null, 2))
            setTestThreshold(res.data.threshold)
            setTestName(res.data.name)
            setGeneratedTest(JSON.stringify(JSON.parse(res.data.generated_test), null, 2))
            setGeneratedFacts(res.data.statements || [])
            setIsFetchedTest(true)
            setRealTestContext(JSON.stringify(JSON.parse(res.data.test_context), null, 2))
        })
      }
  
      const fetchPromptWithVersion = async () => {
        var get_params = '';
        if (promptVersion) {
          get_params = `?version=${promptVersion}`;
        }
        const response = await axios.get(`${API_URL}/prompts/${prompt_id}${get_params}`, {
          headers: {
            Authorization: `Bearer ${await auth.getIdToken()}`
          }
        });
        if (response.status === 200) {
          const fetchedPrompt = response.data;
          setVersion(fetchedPrompt.version);
          setChats(fetchedPrompt.chats);
        }
      };
    
      const fetchPromptWithTimestamp = async () => {
        const response = await axios.get(`${API_URL}/logs/log?prompt_id=${prompt_id}&timestamp=${timestamp}`, {
          headers: {
            Authorization: `Bearer ${await auth.getIdToken()}`
          }
        });
        if (response.status === 200) {
          const log_response = response.data;
          console.log('log_response', log_response);
          setChats(log_response.prompt.chats);
          setTimestamp('test_context');
        }
      };
    
      // Adjusted fetchContext to accept newTimestamp parameter
      const fetchContext = async (newTimestamp = timestamp) => {
        let ts_part = '';
        if (newTimestamp) {
          ts_part = `&timestamp=${newTimestamp}`;
        }
    
        const response = await axios.get(`${API_URL}/logs/context?prompt_id=${prompt_id}${ts_part}`, {
          headers: {
            Authorization: `Bearer ${await auth.getIdToken()}`
          }
        });
        if (response.status === 200) {
          setTestContext(JSON.stringify(response.data, null, 2));
        }
      };
      const handleTimestampChange = async (newTimestamp) => {
        if (newTimestamp === 'test_context'){
          setTimestamp(newTimestamp)
          setTestContext(realTestContext)
        }
        else if (newTimestamp !== undefined) {
          setTimestamp(newTimestamp);
          await fetchContext(newTimestamp);
        }
      };
      // 2. fetch prompt data with version or timestamp from logs
      useEffect(() => {
        const fetchLog = async () => {
          try {
            fetchPromptWithVersion();
            fetchTimestamps();
          } catch (error) {
            console.error('Error fetching prompt:', error);
          }
        };
        fetchLog();
        fetchTestData();
      }, []);
    
      const crumbs = [
        { path: '/', breadcrumb: 'Home' },
        { path: `/tests`, breadcrumb: 'CI/CD' },
        { path: `/tests/${prompt_id}`, breadcrumb: `${prompt_id}` },
        { path: `/tests/${prompt_id}/${test_id}`, breadcrumb: testName },
        { path: window.location.href, breadcrumb: "Edit test" }
      ];
      function tmsToDateTime(tms) {
        tms = parseInt(tms);
        return new Date(tms).toUTCString();
      }

      const generateTest = async () => {
        await axios.post(`${API_URL}/tests?generateTest`,
          {
            ideal_answer: idealAnswer,
            facts: generatedFacts,
          },
          {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        ).then((res) => {
          setGeneratedTest(res.data)
          
        }).catch((err) => console.error(err))
      }
    
      const updateTest = async () => {
        if (testThreshold < 0 || testThreshold > 100){
          toast.error("Invalid threshold value!", TOAST_STYLE_ERROR)
        }
        else{
          try{
            let objectTestContext = JSON.parse(testContext);
            if (idealAnswer === '' && !('ideal_answer' in objectTestContext)){
              toast.error("You should have an ideal answer!", TOAST_STYLE_ERROR)
            }
            else{ 
                try{
                  let objectGenerated = JSON.parse(generatedTest)
                  await axios.put(`${API_URL}/tests?updateTest`,
                    {
                      test_id,
                      test_name: testName,
                      ideal_answer: idealAnswer,
                      generated_test: generatedTest,
                      prompt_data: {
                        prompt_id,
                        prompt_version: promptVersion,
                        chats: promptChats
                      },
                      test_context: JSON.parse(testContext),
                      threshold: testThreshold
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${await auth.getIdToken()}`,
                        'Content-Type': 'application/json'
                      }
                    }
                  ).then((res) => {
                    toast.success("Test has been updated!", TOAST_STYLE_SUCCESS)
                  }).catch((err) => console.error(err))
                }
                catch{
                  toast.error("Generated test is not a JSON!", TOAST_STYLE_ERROR)
                }
            }
        } 
        catch{
          toast.error("Test context is not a JSON!", TOAST_STYLE_ERROR)
        }
      }
      }
    
      const transformFacts = (facts) => {
        return facts.map((item, index) => `${index + 1}. ${item}`).join('\n')
      }

      console.log(timestamp)
    
    return (
        <div className='flex flex-col w-full p-8 space-y-8'>
            <Breadcrumbs crumbs={crumbs} />
            <h2 className='text-2xl font-bold'>Edit: {testName}</h2>
        <div className='grid grid-cols-2 gap-4 p-4 bg-white'>
        <div className='col-span-1'>
          <Input
            value={prompt_id}
            disabled
            className='w-80'
            label='Prompt Id'
          />
          <h2 className="font-bold text-xl mt-4">Prompt chats</h2>
          {promptChats.map((chat, i) => (
            <div key={i} className='mb-4 mt-2 w-full'>
              <Textarea
                className='chat-input-area'
                value={chat.content}
                disabled
                resizable
              />
            </div>
          ))}

          <div className='grid grid-cols-2 gap-4'>
            <div className='col-span-2'>
              <h2 className='text-2xl font-bold'>Ideal answer:</h2>
              <Textarea className='w-full' value={idealAnswer} onChange={(e) => setIdealAnswer(e.target.value)} />
            </div>
            
          </div>

          {isFetchedTest && (
            <div className='w-full'>
              <h2 className='text-2xl font-bold mt-3'>Ideal facts:</h2>
              <Textarea value={transformFacts(generatedFacts)} disabled={true} />
            </div>
          )}

          {isFetchedTest && (
            <div className='w-full'>
              <h2 className='text-2xl font-bold mt-3'>Generated test:</h2>
              {/* <Textarea value={generatedTest} onChange={(e) => setGeneratedTest(e.target.value)} /> */}
              <JsonEditorViewer initialJson={generatedTest} setContext={setGeneratedTest} />
            </div>
          )}

          <div className='flex justify-start items-center mt-5 gap-3'>
            {isFetchedTest && (
              <Input
               label="Test threshold in %" 
               className='w-2/5' 
               value={testThreshold} onChange={(e) => setTestThreshold(e.target.value)} />
            )}
            
            {/* {isGenerateTest && (
              <Loader2 className='animate-spin' />
            )} */}
          </div>
          {isFetchedTest && (
            <div className='flex gap-2 mt-3'>
              <Button className=' bg-green-300'  onClick={() => updateTest()}>
                Save test
              </Button>
              <Button className=" bg-red-300" onClick={() => deleteTest()}>Delete test</Button>
            </div>
              
            )}
        </div>
        <div className='col-span-1'>
              <label htmlFor='timestampSelector' className='block text-sm font-medium text-gray-700 flex items-center gap-2'>
                <h2 className='w-full font-bold text-xl'>Test context:</h2>
                <Calendar /> <span>Select context</span>
                <select
                  id='timestampSelector'
                  value={timestamp || ''}
                  onChange={(e) => handleTimestampChange(e.target.value)}
                  className='mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
                >
                  <option value='test_context'>Test context</option>
                  {logTimestamps.map((ts) => (
                    <option key={ts} value={ts}>
                      {tmsToDateTime(ts)}
                    </option>
                  ))}
                </select>
              </label>
              {isFetchedTimestamps ? <JsonEditorViewer initialJson={testContext} setContext={setTestContext} /> : <Loader2 className='animate-spin' size={25} />}
        </div>
      </div>
        </div>
    )
}

export default CRUDTest